import { useEffect, useState } from "react";
import { Linkedin, MessageCircle, Menu, X } from "lucide-react";
import { ASSETS, COMPANY, NAV_LINKS } from "../data/content";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-[#0A192F]/92 backdrop-blur-xl border-white/10"
          : "bg-[#0A192F]/40 backdrop-blur-md border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
        <div className="flex h-20 items-center justify-between gap-6">
          <a href="#top" data-testid="header-logo-link" className="flex items-center shrink-0">
            <img
              src={ASSETS.logo}
              alt={`${COMPANY.short} logo`}
              className="h-10 w-auto object-contain"
              data-testid="header-logo"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.slice(1)}`}
                className="relative text-[13px] font-medium uppercase tracking-[0.14em] text-slate-200 hover:text-[#D4AF37] transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-[#D4AF37] hover:after:w-full after:transition-[width] after:duration-300"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2">
              <a
                href={COMPANY.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-linkedin"
                aria-label="LinkedIn"
                className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
              >
                <Linkedin size={15} />
              </a>
              <a
                href={COMPANY.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-whatsapp"
                aria-label="WhatsApp"
                className="grid h-9 w-9 place-items-center rounded-full border border-white/15 text-slate-200 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
              >
                <MessageCircle size={15} />
              </a>
            </div>
            <a
              href={`mailto:${COMPANY.emails[1]}?subject=Inspection%20Request`}
              data-testid="header-cta"
              className="hidden md:inline-flex items-center rounded-full bg-[#D4AF37] px-6 py-2.5 text-[12px] font-bold uppercase tracking-[0.14em] text-[#0A192F] hover:bg-[#E8C860] transition-colors duration-300"
            >
              Request Consultation
            </a>
            <button
              onClick={() => setOpen((v) => !v)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
              className="lg:hidden grid h-10 w-10 place-items-center rounded-full border border-white/15 text-white"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden border-t border-white/10 bg-[#0A192F] px-5 py-6"
        >
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                className="py-3 text-sm font-medium uppercase tracking-[0.14em] text-slate-200 hover:text-[#D4AF37] transition-colors duration-300"
              >
                {l.label}
              </a>
            ))}
            <a
              href={`mailto:${COMPANY.emails[1]}?subject=Inspection%20Request`}
              data-testid="mobile-cta"
              className="mt-4 inline-flex justify-center rounded-full bg-[#D4AF37] px-6 py-3 text-xs font-bold uppercase tracking-[0.14em] text-[#0A192F]"
            >
              Request Consultation
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
